import { FC } from 'react';
import { V4 as C } from '@components/v4/tokens';
import type { Nudge } from './usePaulinaChat';

/**
 * PaulinaNudgeCard — el nudge COMPLETO (título + mensaje + CTA) renderizado como TARJETA
 * inline arriba de la conversación dentro del sheet móvil (PaulinaConversation). Es el
 * destino del teaser del header (PaulinaNudgeTeaserMobile): el teaser muestra solo el
 * título; al tocarlo se abre el sheet y aquí se ve el nudge entero con su CTA funcional.
 *
 * Reusa el MISMO molde de render que el PaulinaNudgeBubble de desktop (título en negrita,
 * mensaje, botón de CTA con `nudge.cta.label`) y los MISMOS handlers del hook
 * (`aceptarNudge`/`descartarNudge`), así el CTA funciona idéntico: navegar / abrir chat con
 * prompt / escalar — toda esa lógica vive en `aceptarNudge` de usePaulinaChat. Aquí solo se
 * presenta; no decide el tipo de CTA.
 *
 * Es una tarjeta inline (no fixed, sin caret): se monta dentro del flujo del sheet, no flota.
 */

interface PaulinaNudgeCardProps {
  nudge: Nudge;
  onAccept: (n: Nudge) => void;
  onDismiss: (n: Nudge) => void;
}

export const PaulinaNudgeCard: FC<PaulinaNudgeCardProps> = ({ nudge, onAccept, onDismiss }) => (
  <div
    role="group"
    aria-label={nudge.titulo}
    className="mx-1 mt-2 rounded-xl p-3"
    style={{ background: C.primary, color: C.white, boxShadow: '0 8px 24px rgba(37,55,70,.18)' }}
  >
    <div className="mb-1 flex items-start justify-between gap-2">
      <p className="text-sm font-bold leading-snug">{nudge.titulo}</p>
      <button
        type="button"
        aria-label="Descartar"
        onClick={() => onDismiss(nudge)}
        style={{ background: 'transparent', border: 0, color: 'rgba(255,255,255,.7)', cursor: 'pointer', padding: 0 }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>
    </div>
    <p className="mb-3 text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,.82)' }}>
      {nudge.mensaje}
    </p>
    <button
      type="button"
      onClick={() => onAccept(nudge)}
      className="w-full rounded-lg px-3 py-2 text-sm font-semibold"
      style={{ background: C.white, color: C.primary }}
    >
      {nudge.cta.label}
    </button>
  </div>
);
